import { FormProvider } from "react-hook-form";
import { DialogContent, DialogHeader } from "@/view/components/ui/dialog";
import useOffersInssModal from "./useOffersInssModal";
import { Button } from "@/view/components/ui/button";
import { Input } from "@/view/components/ui/input";
import { Label } from "@/view/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/view/components/ui/select";
import { addMoneySimbols } from "@/app/utils/formatMoneyValue";
import { Skeleton } from "@/view/components/ui/skeleton";
import { Copy } from "lucide-react";
import Header from "../PersonalInfoModal/components/Header";
import { StepProgress } from "../components/StepProgress";

interface OffersInssModalProps {
  onContinue: () => void;
  onBack: () => void;
}

export default function OffersInssModal({
  onContinue,
  onBack,
}: OffersInssModalProps) {
  const {
    form,
    submitModal,
    isPending,
    isFetching,
    installments,
    selectedInstallment,
    handleSelectInstallment,
    displayedValue,
    installmentInput,
    installmentValue,
    handleInstallmentValueChange,
    commission,
    matricula,
    digitoMatricula,
    manualRegistration,
    setManualRegistration,
    manualRegistrationDigit,
    setManualRegistrationDigit,
    banksInfos,
    selectedBankCodigo,
    handleSelectBank,
    consentLink,
    handleCopyConsentLink,
    errorMessage,
    isManualRegistrationValid,
    requiresConsent,
    authorizedConsent,
    isConsentAuthorized,
    consentLoading,
    handleVerifyConsent,
    steps,
    isError,
    retry,
    cancel,
  } = useOffersInssModal({ onContinue });

  return (
    <DialogContent
      className="flex max-h-[90vh] flex-col overflow-y-auto sm:max-w-xl"
      closeOnClickOutside={!isFetching}
    >
      <DialogHeader>
        <Header
          title="Opções de crédito"
          onBack={() => {
            cancel();
            onBack();
          }}
        />
      </DialogHeader>

      {errorMessage || isError ? (
        <div className="flex flex-col items-center justify-center gap-6 py-10">
          <p className="text-center text-base font-semibold text-black01">
            {errorMessage ?? "Não foi possível realizar a simulação."}
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                cancel();
                onBack();
              }}
              type="button"
              className="w-[140px] rounded-full text-primary"
            >
              Voltar
            </Button>
            <Button
              onClick={() => retry()}
              type="button"
              className="w-[140px] rounded-full"
            >
              Tentar novamente
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={submitModal} className="flex flex-1 flex-col">
          <FormProvider {...form}>
            <div>
              {isFetching && (
                <StepProgress
                  steps={steps}
                  title="Simulando na Crefisa"
                  className="mb-4"
                />
              )}
              <div>
                <Label className="text-sm font-medium text-gray-500">
                  Valor solicitado
                </Label>
                <Input
                  className="mt-2 text-xl"
                  value={displayedValue ? addMoneySimbols(displayedValue) : ""}
                  placeholder="Selecione uma parcela"
                  readOnly
                  disabled
                />
              </div>

              <div className="mt-4">
                <Label className="text-sm font-medium text-gray-500">
                  Valor da parcela
                </Label>
                <div className="mt-2 flex gap-2">
                  <Input
                    className="flex-1 text-base md:text-xl"
                    value={
                      installmentInput ? addMoneySimbols(installmentValue) : ""
                    }
                    onChange={(e) =>
                      handleInstallmentValueChange(e.target.value)
                    }
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                  />
                  <div className="w-[120px] shrink-0">
                    {isFetching ? (
                      <Skeleton className="h-10 w-full rounded-md" />
                    ) : (
                      <Select
                        value={selectedInstallment?.idSimulacao ?? ""}
                        onValueChange={handleSelectInstallment}
                        disabled={!installments.length}
                      >
                        <SelectTrigger className="h-full">
                          <SelectValue placeholder="Parcelas" />
                        </SelectTrigger>
                        <SelectContent>
                          {installments.map((i) => (
                            <SelectItem
                              key={i.idSimulacao}
                              value={i.idSimulacao}
                            >
                              {i.quantidadeParcelas}x
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                {!isFetching && installments.length === 0 && (
                  <p className="mt-2 text-sm font-medium">
                    Nenhuma parcela disponível
                  </p>
                )}
              </div>

              {typeof commission === "number" && displayedValue > 0 && (
                <p className="mt-4 text-sm font-medium text-black01">
                  Comissão ({commission}%):{" "}
                  <span className="font-bold">
                    {addMoneySimbols((commission / 100) * displayedValue)}
                  </span>
                </p>
              )}

              {(!matricula || !digitoMatricula) && (
                <div className="mt-4 flex gap-3">
                  <Label className="w-[70%] text-sm font-medium">
                    NIS
                    <Input
                      className="mt-2"
                      placeholder="Informe o NIS"
                      maxLength={11}
                      value={manualRegistration}
                      onChange={(e) =>
                        setManualRegistration(e.target.value.replace(/\D/g, ""))
                      }
                    />
                  </Label>
                  <Label className="w-[30%] text-sm font-medium">
                    Dígito
                    <Input
                      className="mt-2"
                      placeholder="Dígito"
                      maxLength={1}
                      inputMode="numeric"
                      value={manualRegistrationDigit}
                      onChange={(e) =>
                        setManualRegistrationDigit(
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                    />
                  </Label>
                </div>
              )}

              {requiresConsent && (
                <>
                  <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
                    <p className="text-sm font-semibold text-black01">
                      É obrigatório realizar o consentimento para finalizar a
                      proposta deste cliente.
                    </p>
                  </div>

                  {isConsentAuthorized && authorizedConsent ? (
                    <div className="mt-4 rounded-lg border border-green-300 bg-green-50 p-3">
                      <p className="text-sm font-semibold text-black01">
                        Consentimento autorizado pelo cliente
                      </p>
                      <p className="mt-1 text-sm font-medium text-black04">
                        {`${authorizedConsent.nomeBanco} · Ag. ${authorizedConsent.agencia} · Conta ${authorizedConsent.conta}-${authorizedConsent.digitoConta}`}
                      </p>
                    </div>
                  ) : consentLink ? (
                    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <p className="text-sm font-semibold text-black01">
                        Esse cliente precisa fazer o consentimento até o final
                        da digitação:
                      </p>
                      <button
                        type="button"
                        onClick={handleCopyConsentLink}
                        className="mt-2 flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
                      >
                        <Copy className="h-4 w-4" />
                        Copiar link do consentimento
                      </button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleVerifyConsent}
                        isLoading={consentLoading}
                        disabled={consentLoading}
                        className="mt-3 w-full rounded-full text-primary"
                      >
                        Verificar consentimento
                      </Button>
                    </div>
                  ) : banksInfos.length > 0 ? (
                    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <p className="text-sm font-semibold text-black01">
                        Nenhum consentimento encontrado para o cliente
                      </p>
                      <p className="mt-1 text-sm font-medium text-black04">
                        Caso deseje selecione o banco para enviar o
                        consentimento
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {banksInfos.map((bank) => (
                          <Button
                            key={bank.codigo}
                            type="button"
                            variant={
                              selectedBankCodigo === bank.codigo
                                ? "default"
                                : "outline"
                            }
                            disabled={isPending}
                            onClick={() => handleSelectBank(bank.codigo)}
                            className="rounded-full"
                          >
                            {bank.nome}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleVerifyConsent}
                        isLoading={consentLoading}
                        disabled={consentLoading}
                        className="w-full rounded-full text-primary"
                      >
                        Verificar consentimento
                      </Button>
                    </div>
                  )}
                </>
              )}

              <div className="mt-6 flex justify-end gap-2">
                <Button
                  disabled={
                    isPending ||
                    !selectedInstallment ||
                    installments?.length === 0 ||
                    !isManualRegistrationValid ||
                    (requiresConsent && !isConsentAuthorized)
                  }
                  type="submit"
                  className="w-[160px] rounded-full bg-primary font-semibold text-white"
                >
                  Avançar
                </Button>
              </div>
            </div>
          </FormProvider>
        </form>
      )}
    </DialogContent>
  );
}
